import {useCallback, useEffect, useRef, useState} from 'react';

import {getAccountUserHistoryByType} from 'src/openapi/admin/apis/GetAccountUserHistoryByTypeApi';
import {useAppDispatch} from 'src/store/hooks';

import type {HistoryGrouping} from './chart-period';
import type {DailyUserCountByType} from 'src/openapi/admin/models/DailyUserCountByType';
import type {Error as ApiError} from 'src/openapi/admin/models/Error';

type Props = {
    accountId: number,
    startDate: string,
    endDate: string,
    groupBy: HistoryGrouping,
    enabled: boolean,
};
type Result = {key: string, rows: DailyUserCountByType[], error: ApiError | Error | null};

export const useAccountUserHistoryChartData = ({accountId, startDate, endDate, groupBy, enabled}: Props) => {
    const dispatch = useAppDispatch();
    const [attempt, setAttempt] = useState(0);
    const [result, setResult] = useState<Result | null>(null);
    const cached = useRef<Result | null>(null);
    const key = `${accountId}:${startDate}:${endDate}:${groupBy}:${attempt}`;

    useEffect(() => {
        if (!enabled || cached.current?.key === key) {
            return;
        }
        const controller = new AbortController();
        const load = async () => {
            let next: Result;
            try {
                const {payload} = await dispatch(getAccountUserHistoryByType(
                    {accountId, startDate, endDate, groupBy}, {signal: controller.signal},
                ));
                next = {key, rows: payload.error ? [] : payload.data, error: payload.error ? payload.data : null};
            } catch (error) {
                next = {key, rows: [], error: error instanceof Error ? error : new Error('Unable to load history')};
            }
            if (!controller.signal.aborted) {
                cached.current = next;
                setResult(next);
            }
        };
        load();
        return () => controller.abort();
    }, [dispatch, accountId, startDate, endDate, groupBy, enabled, key]);

    const loadData = useCallback(() => setAttempt(value => value + 1), []);
    const current = result?.key === key ? result : null;
    return {
        rows: current?.rows ?? [],
        isLoading: enabled && !current,
        loadingError: current?.error ?? null,
        loadData,
    };
};
